import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { ApiResponse } from '../../../../interfaces/models/models/paginated-response.model';
import { IPaginationPayload } from '../../../../interfaces/shared/IPaginationPayload';
import { IPurchases } from '../../../../interfaces/purchases/IPurchases';
import { IPurchaseListState } from '../../../../interfaces/purchases/IPurchaseList';
import { PurchasesService } from './PurchasesApi';


const initialState: IPurchaseListState = {
    purchaseIsLoading: false,
    purchasesAreLoading: false,
    purchasesInvoicesAreLoading: false,
    requestStatus: 'default',
    message: '',
};

export const tryToFetchPurchases = createAsyncThunk<any, IPaginationPayload>(
    'purchases/tryToFetchPurchases',
    async ({ pageNumber, pageSize, data }) => {
        const result = await PurchasesService.tryToFetchPurchases(pageNumber, pageSize, data);
        return result?.data;
    },
);

export const tryToFetchPurchasesAndInvoices = createAsyncThunk<any, IPaginationPayload>(
    'purchases/tryToFetchPurchasesAndInvoices',
    async ({ pageNumber, pageSize, data }) => {
        const result = await PurchasesService.tryToFetchPurchasesAndInvoices(pageNumber, pageSize, data);
        return result?.data;
    },
);

export const tryToFetchSinglePurchase = createAsyncThunk<IPurchases, string>(
    'purchases/tryToFetchSinglePurchase',
    async (id: string) => {
        const result = await PurchasesService.tryToFetchSinglePurchase(id);
        return result?.data?.data;
    },
);


export const tryToFetchPurchaseMismatch = createAsyncThunk<IPurchases, string>(
    'purchases/tryToFetchPurchaseMismatch',
    async (id: string) => {
        const result = await PurchasesService.tryToFetchPurchaseMismatch(id);
        return result?.data?.data;
    },
);


export const tryToDownloadPurchaseDetails = createAsyncThunk<IPurchases, string>(
    'purchases/tryToDownloadPurchaseDetails',
    async (id: string) => {
        const result = await PurchasesService.tryToDownloadPurchaseDetails(id);
        return result?.data;
    },
);

export const tryToCreatePurchase = createAsyncThunk<ApiResponse<IPurchases>, any>(
    'purchases/tryToCreatePurchase',
    async (data: IPurchases, { rejectWithValue }) => {
        try {
            const result = await PurchasesService.tryToCreatePurchase(data);
            return result.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToEditPurchase = createAsyncThunk<ApiResponse<IPurchases>, { id: string, data: any }>(
    'purchases/tryToEditPurchase',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const result = await PurchasesService.tryToEditPurchase(id, data);
            return result.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToDeletePurchase = createAsyncThunk<ApiResponse<IPurchases>, string>(
    'purchases/tryToDeletePurchase',
    async (id, { rejectWithValue }) => {
        try {
            const result = await PurchasesService.tryToDeletePurchase(id);
            return result?.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToUploadCashCreditFileCompare = createAsyncThunk<any, any>(
    'purchases/tryToUploadCashCreditFileCompare',
    async (data, { rejectWithValue }) => {
        try {
            const result = await PurchasesService.tryToUploadCashCreditFileCompare(data);
            return result.data?.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToUploadPurchaseInvoiceFileCompare = createAsyncThunk<any, any>(
    'purchases/tryToUploadPurchaseInvoiceFileCompare',
    async (data, { rejectWithValue }) => {
        try {
            const result = await PurchasesService.tryToUploadPurchaseInvoiceFileCompare(data);
            return result.data?.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const purchasesSlice = createSlice({
    name: 'purchases',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // tryToFetchPurchases
            .addCase(tryToFetchPurchases.pending, (state) => {
                state.purchasesAreLoading = true;
            })
            .addCase(tryToFetchPurchases.rejected, (state) => {
                state.purchasesAreLoading = false;
            })
            .addCase(tryToFetchPurchases.fulfilled, (state, action) => {
                state.purchasesAreLoading = false;
                state.purchases = action.payload;
            })

            // tryToFetchPurchasesAndInvoices
            .addCase(tryToFetchPurchasesAndInvoices.pending, (state) => {
                state.purchasesInvoicesAreLoading = true;
            })
            .addCase(tryToFetchPurchasesAndInvoices.rejected, (state) => {
                state.purchasesInvoicesAreLoading = false;
            })
            .addCase(tryToFetchPurchasesAndInvoices.fulfilled, (state, action) => {
                state.purchasesInvoicesAreLoading = false;
                state.purchaseInvoices = action.payload;
            })


            // tryToFetchSinglePurchase
            .addCase(tryToFetchSinglePurchase.pending, (state) => {
                state.purchaseIsLoading = true;
            })
            .addCase(tryToFetchSinglePurchase.rejected, (state) => {
                state.purchaseIsLoading = false;
            })
            .addCase(tryToFetchSinglePurchase.fulfilled, (state, action) => {
                state.purchaseIsLoading = false;
                state.purchaseDetails = action.payload;
            })

            // tryToFetchPurchaseMismatch
            .addCase(tryToFetchPurchaseMismatch.pending, (state) => {
                state.purchaseIsLoading = true;
            })
            .addCase(tryToFetchPurchaseMismatch.rejected, (state) => {
                state.purchaseIsLoading = false;
            })
            .addCase(tryToFetchPurchaseMismatch.fulfilled, (state, action) => {
                state.purchaseIsLoading = false;
                state.purchaseMismatch = action.payload;
            })

            // tryToCreatePurchase
            .addCase(tryToCreatePurchase.pending, (state) => {
                state.purchaseIsLoading = true;
            })
            .addCase(tryToCreatePurchase.rejected, (state) => {
                state.purchaseIsLoading = false;
            })
            .addCase(tryToCreatePurchase.fulfilled, (state, action) => {
                state.purchaseIsLoading = false;
                state.requestStatus = 'success';
                const data = action.payload.data || action.meta.arg;
                if (state.purchases?.data) state.purchases.data.elements = [...state.purchases?.data.elements, data];
            })

            // tryToEditPurchase
            .addCase(tryToEditPurchase.pending, (state) => {
                state.purchaseIsLoading = true;
            })
            .addCase(tryToEditPurchase.rejected, (state) => {
                state.purchaseIsLoading = false;
            })
            .addCase(tryToEditPurchase.fulfilled, (state) => {
                state.purchaseIsLoading = false;
            })

            // tryToDeletePurchase
            .addCase(tryToDeletePurchase.pending, (state) => {
                state.purchaseIsLoading = true;
            })
            .addCase(tryToDeletePurchase.rejected, (state) => {
                state.purchaseIsLoading = false;
            })
            .addCase(tryToDeletePurchase.fulfilled, (state, action) => {
                state.purchaseIsLoading = false;
                state.requestStatus = 'success';
                if (state.purchases?.data.elements)
                    state.purchases.data.elements = state.purchases?.data.elements.filter((purchase) => purchase?._id !== action.meta?.arg)
            })
    },
});

export default purchasesSlice.reducer;